'use client';

import { FC, useContext, useState } from 'react';
import { Briefcase, Globe, Mail, MapPin, Pencil, Phone, User } from 'lucide-react';
import { ProductsContext } from '../context';
import { useContact, useUpdateContact } from '@/hooks/useContact';
import { Skeleton } from '@/components/ui/skeleton';
import ContactEditForm from './contact-panel/ContactEditForm';
import { toast } from 'react-toastify';

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase() || 'UV';
};

const Section: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <section className="mt-5 border-t border-slate-100 pt-4">
    <h3 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-slate-400">{label}</h3>
    {children}
  </section>
);

const ContactPanel: FC = () => {
  const { activeThread, token, setActiveThread } = useContext(ProductsContext);
  const threadId = activeThread?.threadId || null;
  const { data: contactData, isLoading } = useContact(threadId);
  const updateContact = useUpdateContact(token);
  const [isEditing, setIsEditing] = useState(false);
  const [editFields, setEditFields] = useState({
    fullName: '',
    firstName: '',
    lastName: '',
    phone: '',
    jobTitle: '',
    status: '',
    notes: '',
  });

  if (!threadId) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center text-slate-400">
        <User className="mb-2 h-5 w-5 text-slate-300" />
        <p className="text-[12px] font-medium">No conversation selected</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-5 p-5">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-4 flex-1" />
        </div>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const contactInfo = contactData?.contactInfo || {
    fullName: activeThread?.title || '',
    email: activeThread?.userEmail || '',
  };
  const company = contactInfo.company || activeThread?.companyInfo?.name || null;
  const website = contactInfo.website || activeThread?.companyInfo?.domain || null;
  const displayName = contactInfo.fullName || contactInfo.email || activeThread?.title || 'Unknown Visitor';
  const location = contactInfo.location
    || [contactInfo.city, contactInfo.stateProvince, contactInfo.country].filter(Boolean).join(', ')
    || null;
  const employeeRange = activeThread?.companyInfo?.employeeRange
    ? `${activeThread.companyInfo.employeeRange.replace(/(\d)-(\d)/g, '$1–$2')}${activeThread.companyInfo.employeeRange.toLowerCase().includes('employee') ? '' : ' employees'}`
    : null;
  const companyMeta = [activeThread?.companyInfo?.industry, employeeRange]
    .filter(Boolean)
    .join(' · ');
  const rawSource = contactInfo.registrationSource || contactInfo.registrationSourceDetail || 'Chatbot';
  const source = `${rawSource.charAt(0).toUpperCase()}${rawSource.slice(1)}`;

  const handleEditClick = () => {
    setEditFields({
      fullName: contactInfo.fullName || '',
      firstName: contactInfo.firstName || '',
      lastName: contactInfo.lastName || '',
      phone: contactInfo.phone || '',
      jobTitle: contactInfo.jobTitle || '',
      status: contactInfo.status || '',
      notes: contactInfo.notes || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!threadId) return;
    try {
      await updateContact.mutateAsync({ threadId, data: editFields });
      if (activeThread && editFields.fullName && editFields.fullName !== contactInfo.fullName) {
        setActiveThread({ ...activeThread, title: editFields.fullName });
      }
      setIsEditing(false);
    } catch {
      toast.error('Could not update this contact. Please try again.');
    }
  };

  if (isEditing) {
    return (
      <div className="p-5">
        <h2 className="mb-5 text-[14px] font-semibold text-slate-900">Edit contact</h2>
        <ContactEditForm
          editFields={editFields}
          setEditFields={setEditFields}
          onSave={handleSave}
          onCancel={() => setIsEditing(false)}
          isSaving={updateContact.isPending}
        />
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#EEF3FF] text-[13px] font-semibold text-[#0848E7]">
          {getInitials(displayName)}
        </div>
        <h2 className="min-w-0 flex-1 truncate text-[14px] font-semibold text-slate-900">{displayName}</h2>
        <button
          type="button"
          onClick={handleEditClick}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          title="Edit contact"
          aria-label="Edit contact"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-4 space-y-2.5">
        {contactInfo.email && (
          <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-2.5 text-[13px] text-slate-600 hover:text-[#0848E7]">
            <Mail className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
            <span className="min-w-0 truncate">{contactInfo.email}</span>
          </a>
        )}
        {contactInfo.phone && (
          <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-2.5 text-[13px] text-slate-600 hover:text-[#0848E7]">
            <Phone className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
            <span className="min-w-0 truncate">{contactInfo.phone}</span>
          </a>
        )}
        {contactInfo.jobTitle && (
          <div className="flex items-start gap-2.5 text-[13px] text-slate-600">
            <Briefcase className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
            <span className="min-w-0">{contactInfo.jobTitle}</span>
          </div>
        )}
        {location && (
          <div className="flex items-start gap-2.5 text-[13px] text-slate-600">
            <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
            <span className="min-w-0">{location}</span>
          </div>
        )}
        {website && (
          <a
            href={website.startsWith('http') ? website : `https://${website}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2.5 text-[13px] text-slate-600 hover:text-[#0848E7]"
          >
            <Globe className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
            <span className="min-w-0 truncate">{website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</span>
          </a>
        )}
      </div>

      <Section label="Company">
        <p className="text-[13px] text-slate-700">{company || 'Unknown'}</p>
        {companyMeta && <p className="mt-0.5 text-[12.5px] text-slate-500">{companyMeta}</p>}
      </Section>

      <Section label="Source">
        <p className="text-[13px] text-slate-700">{source}</p>
      </Section>

      {contactInfo.status && (
        <Section label="Status">
          <p className="text-[13px] capitalize text-slate-700">{contactInfo.status}</p>
        </Section>
      )}

      <Section label="Notes">
        <p className="whitespace-pre-wrap text-[13px] leading-[1.6] text-slate-600">
          {contactInfo.notes || 'No notes yet.'}
        </p>
      </Section>
    </div>
  );
};

export default ContactPanel;
